import React, { Component, useEffect, useState } from "react";
import { format } from "date-fns";

import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Link, Tab } from "@mui/material";

import styled from "styled-components";
import { StyledButton } from "../BuildlingBlocks/StyledButton";
import { ResetIcon } from "../Icon/ResetIcon";
import { RunIcon } from "../Icon/RunIcon";
import { TestResultData } from "../../rule-engine-core/Common/Testing/TestResultData";
import { BaseTestResultData } from "../../rule-engine-core/Common/Testing/BaseTestResultData";
import { TestContext } from "./TestContext";

import { ExecutionData } from "../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";

import { AutoBind } from "../../rule-engine-core/Common/AutoBind";

import { OpenBlueIcon } from "../Icon/OpenBlueIcon";
import { DropdownBlueIcon } from "../Icon/DropdownBlueIcon";
import { EventLog, EventLogType } from "../../rule-engine-core/Common/Event/EventLog";
import { AsyncExecutable } from "../../rule-engine-core/Common/Execution/AsyncExecutable";
import { Executable } from "../../rule-engine-core/Common/Execution/Executable";
import { RuleExecutable } from "../../rule-engine-core/Common/Execution/RuleExecutable";
import { DefinitionBasedDataEditor } from "../DataEditing/DefinitionBasedDataEditor";
import { MultiExecutable } from "../../rule-engine-core/Common/Execution/MultiExecutable";
import { TestDisplayFactory } from "./TestDisplayFactory";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { DefineUnitTest } from "./DefineUnitTest";
import { UnitTestResultNode } from "../../rule-engine-core/Common/UnitTesting/UnitTestResultNode";
import { UnitTestConfiguration } from "../../rule-engine-core/Common/UnitTesting/UnitTestConfiguration";
import { FlowConfiguration } from "../../rule-engine-core/Flow/FlowConfiguration";
import { FlowEngine } from "../../rule-engine-core/Flow/Engine/FlowEngine";
import { AppGlueCorePlugins } from "../../rule-engine-core/AppGlueCorePlugins";
import { ITestResultItem } from "../../rule-engine-core/Common/Testing/ITestResultItem";
import { FormConfiguration } from "../../Form/FormConfiguration";
import { FormTestResult } from "../../Form/Structure/FormTestResult";
import { UICollapse } from "../BuildlingBlocks/UICollapse";
import type { AppGlueAny } from "../../rule-engine-core/Common/AppGlueAny";

function getNormalizedTitle(title?: string) {
    return (title || "").replace(/_/g, " ");
}

export type XExecutableTesterProps = {
    executable: RuleExecutable | AsyncExecutable | Executable | MultiExecutable | FlowConfiguration;
    builderName?: string;
    saveUnitTest?: (unitTest: UnitTestConfiguration) => void;
    startData?: object;
    getTabs?: () => {
        ui: JSX.Element;
        name: string;
    }[];
};

export class XExecutableTester extends Component<
    XExecutableTesterProps,
    {
        openInputLayer: boolean;
        testcontext: TestResultData;
        value: BaseTestResultData | undefined;
        data: {
            [key: string]: AppGlueAny;
        };
        currentView: string;
        tabs: { ui: JSX.Element; name: string }[];
    }
> {
    constructor(props: XExecutableTesterProps) {
        super(props);

        this.state = {
            testcontext: new TestResultData(),
            value: undefined,
            data: props.startData ?? props.executable.getInputDefinition().getValueObject(),
            currentView: "UI",
            tabs: [
                { name: "UI", ui: <></> },
                {
                    name: "Events",
                    ui: <></>,
                },
            ],
            openInputLayer: false,
        };
    }

    @AutoBind
    onStart(newdata: object | undefined): void {
        if (!newdata) {
            this.setState({
                data: {},
            });
            return;
        }

        this.setState({
            data: newdata,
        });

        //  FormHost.defaultFormHost = undefined;
        try {
            if (this.props.executable instanceof FlowConfiguration) {
                let promise = FlowEngine.startFlow(
                    AppGlueCorePlugins.UserContext.getCurrentUser(),
                    this.props.executable as FlowConfiguration,
                    new ExecutionData(newdata),
                    this.state.testcontext,
                );
                if (promise instanceof Promise) {
                    promise.then(() => {
                        // todo: check the result with currentView, because we can not finish and move to other tester.
                        this.forceUpdate();
                    });
                }
            } else if (this.props.executable instanceof FormConfiguration) {
                let subContext: FormTestResult | undefined;

                if (this.state.testcontext) {
                    subContext = new FormTestResult(
                        this.props.executable as FormConfiguration,
                        new ExecutionData(newdata),
                    );
                    this.state.testcontext.addTestResultItem(subContext);
                    subContext.addLogItem(new EventLog(`Form: start evaluating`));
                }
            } else {
                let promise = this.props.executable.run(new ExecutionData(newdata), this.state.testcontext);

                if (promise instanceof Promise) {
                    promise.then(() => {
                        // todo: check the result with currentView, because we can not finish and move to other tester.
                        this.forceUpdate();
                    });
                }
            }
        } catch (e) {
            console.log(e);
        }

        if (this.state.testcontext.items.length > 0) {
            this.onSelect(this.state.testcontext.items[this.state.testcontext.items.length - 1]);
        }
    }

    @AutoBind
    onReset(event: React.MouseEvent<HTMLButtonElement>): void {
        event.stopPropagation();

        this.setState({
            testcontext: new TestResultData(),
            value: undefined,
        });
    }

    @AutoBind
    onSelect(selecteditem: BaseTestResultData) {
        let tabs: { name: string; ui: JSX.Element }[] = [
            { name: "UI", ui: TestDisplayFactory.renderTestDisplay(selecteditem) ?? <></> },
            {
                name: "Events",
                ui: (
                    <EventsRender>
                        {(selecteditem?.logs || []).map((log) => (
                            <EventRender
                                type={log.type}
                                key={log.when.getTime()}
                            >
                                {format(log.when, "dd.MM.yy HH:mm:ss.sss")}: {log.message}
                            </EventRender>
                        ))}
                    </EventsRender>
                ),
            },
        ];

        if (selecteditem && TestDisplayFactory.renderTabs(selecteditem)) {
            tabs = TestDisplayFactory.renderTabs(selecteditem)!;
        }

        this.setState({ value: selecteditem, tabs, currentView: tabs[0].name });
    }

    @AutoBind
    renderTree() {
        return (
            <TesterTreeView>
                {this.state.testcontext.items.length === 0 && (
                    <TesterStatus>
                        Welcome to the debugging system. Enter first data and push on "Play" to starting testing
                    </TesterStatus>
                )}

                {this.state.testcontext.items
                    .sort((a, b) => {
                        if (a.when > b.when) return -1;
                        return 1;
                    })
                    .map((item, idx) => (
                        <TesterTreeItem
                            key={`${idx}-${item.when.getTime()}`}
                            data={this.state.data}
                            item={item}
                            onSelect={this.onSelect}
                            builderName={this.props.builderName}
                        />
                    ))}
            </TesterTreeView>
        );
    }

    @AutoBind
    onChangeView(_event: React.ChangeEvent<{}>, value: string) {
        this.setState({
            currentView: value,
        });
    }

    @AutoBind
    onOpenInputLayer(event: React.MouseEvent<HTMLDivElement>) {
        event.stopPropagation();

        this.setState({
            openInputLayer: true,
        });
    }

    render() {
        // render side tree
        // render run button
        // render ui for parameters to be gathered
        // when its run, find the first node with a ui and render the ui to the right

        return (
            <TestContext.Provider value={this.state.value}>
                <TabContext value={this.state.currentView}>
                    <Render>
                        <TesterLeftSide>
                            <TesterControls>
                                <div>Debugging</div>
                                <TestActions>
                                    <StyledButton
                                        endIcon={<ResetIcon />}
                                        size="small"
                                        onClick={this.onReset}
                                    >
                                        Reset
                                    </StyledButton>

                                    <RunButton
                                        data-testid="XExecutableTester-play"
                                        onClick={this.onOpenInputLayer}
                                    >
                                        Play
                                        <RunIcon />
                                    </RunButton>

                                    <DefinitionBasedDataEditor
                                        data={this.props.executable.getInputDefinition()}
                                        value={this.state.data}
                                        open={this.state.openInputLayer}
                                        title={"Test Data"}
                                        updateCallback={(newValue) => {
                                            this.setState({
                                                openInputLayer: false,
                                            });
                                            this.onStart(newValue);
                                        }}
                                        doneButtonTitle="Run"
                                        onClose={() => {
                                            this.setState({
                                                openInputLayer: false,
                                            });
                                        }}
                                    />
                                </TestActions>
                            </TesterControls>
                            {this.renderTree()}
                        </TesterLeftSide>
                        <TesterRender>
                            <TabList
                                style={{ height: "32px" }}
                                onChange={this.onChangeView}
                            >
                                {this.state.tabs.map((tab) => (
                                    <Tab
                                        value={tab.name}
                                        label={tab.name}
                                        key={tab.name}
                                    />
                                ))}
                            </TabList>
                            <BuilderRender>
                                {this.state.tabs.map((tab: { name: string; ui: JSX.Element }, idx: number) => (
                                    <TabPanel
                                        style={{ height: "100%" }}
                                        value={tab.name}
                                        key={idx}
                                    >
                                        {tab.ui}
                                    </TabPanel>
                                ))}
                            </BuilderRender>
                        </TesterRender>
                    </Render>
                </TabContext>
            </TestContext.Provider>
        );
    }
}

interface ITesterTreeItemProps {
    item: BaseTestResultData;
    data: object;
    builderName?: string;
    saveUnitTest?: (unitTest: UnitTestConfiguration) => void;

    onSelect: (item: BaseTestResultData) => void;
}

const TesterTreeItem = (props: ITesterTreeItemProps) => {
    const [iscollapsed, setIsCollapsed] = useState<boolean>(true);

    const [defineTest, setDefineTest] = useState<boolean>(false);

    const testContext = React.useContext(TestContext);

    const isclickable = !!TestDisplayFactory.renderTestDisplay(props.item);

    const subitems = props.item.items;

    const haschildren: boolean = !!subitems.find((item) => item instanceof BaseTestResultData);

    const onSelect = (item: BaseTestResultData) => {
        props.onSelect(item);
    };

    const onToggle = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();

        if (haschildren) {
            setIsCollapsed(!iscollapsed);
        }
    };

    const [unitTest, setUnitTest] = useState<UnitTestConfiguration>();
    useEffect(() => {
        setUnitTest(new UnitTestConfiguration(UnitTestResultNode.createResultNode(props.item), props.data));
    }, [props.item]);

    return (
        <TesterSection
            data-testid={`treeitem-${props.item.type}`}
            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                event.stopPropagation();
                if (isclickable) {
                    onSelect(props.item);
                } else if (subitems.length > 0) {
                    let firstitem = subitems.find(
                        (it: ITestResultItem) => it instanceof BaseTestResultData,
                    ) as BaseTestResultData;
                    if (firstitem) {
                        onSelect(firstitem);
                    }

                    setIsCollapsed(false);
                }
            }}
        >
            <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                paddingTop={"4px"}
                paddingBottom={"4px"}
                paddingLeft={"8px"}
                paddingRight={"8px"}
                bgcolor={
                    testContext === props.item ? UIStyleConstants.BLUE_VERY_LIGHT_COLOR : UIStyleConstants.WHITE_COLOR
                }
            >
                <Box
                    display={"flex"}
                    flexDirection={"row"}
                >
                    <ExpandIconButton
                        visible={haschildren}
                        onClick={onToggle}
                    >
                        {iscollapsed ? <OpenBlueIcon /> : <DropdownBlueIcon />}
                    </ExpandIconButton>
                    <TestedItemTitle>
                        {getNormalizedTitle(props.item.type)} ({props.item.getDuration()})
                        {props.item.isShowResult && (
                            <>
                                <br />
                                Result: {props.item.result}
                            </>
                        )}
                    </TestedItemTitle>
                </Box>
                {testContext === props.item && <Link onClick={() => setDefineTest(true)}>+ Unit Test</Link>}
            </Box>
            {haschildren && (
                <UICollapse in={!iscollapsed}>
                    <TesterSectionBody>
                        {subitems
                            .filter((i) => {
                                return !i.hidden;
                            })
                            .map((subi, index) => {
                                if (subi instanceof BaseTestResultData) {
                                    return (
                                        <TesterTreeItem
                                            key={`${index}_${subi.when.getTime()}`}
                                            item={subi}
                                            onSelect={props.onSelect}
                                            builderName={props.builderName}
                                            data={props.data}
                                        />
                                    );
                                }
                                return <React.Fragment key={`${index}_${subi.when.getTime()}`}></React.Fragment>;
                            })}
                    </TesterSectionBody>
                </UICollapse>
            )}
            {unitTest && (
                <DefineUnitTest
                    open={defineTest}
                    onClose={() => setDefineTest(false)}
                    onTestSave={() => {
                        if (props.saveUnitTest && unitTest) props.saveUnitTest(unitTest);
                        setDefineTest(false);
                    }}
                    unitTest={unitTest}
                />
            )}
        </TesterSection>
    );
};

const TesterSectionBody = styled.div`
    padding-left: 8px;
`;

const ExpandIconButton = styled.div<{ visible: boolean }>`
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    visibility: ${(props) => (props.visible ? "visible" : "hidden")};
`;

const TesterRender = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;

    overflow: hidden;

    padding: 12px 16px;

    flex: 1;

    .MuiTabs-fixed {
        display: flex;
        align-items: center;
    }

    .MuiTabs-flexContainer {
        background: ${UIStyleConstants.WHITE_COLOR};
        border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        box-sizing: border-box;
        border-radius: 4px;
    }

    .MuiTabs-indicator {
        display: none;
    }

    .MuiTab-root {
        min-width: 60px;
        min-height: 0;

        //styleName: text/Inter Regular 12px;

        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 17px;
        letter-spacing: 0px;
        text-align: center;

        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

        &.Mui-selected {
            background: ${UIStyleConstants.BLUE_COLOR};
            color: ${UIStyleConstants.WHITE_COLOR};
        }
    }
`;

const TesterLeftSide = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;

    overflow: hidden;

    padding: 0 24px;
    border-right: solid 1px ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};

    background: ${UIStyleConstants.WHITE_COLOR};

    min-width: 425px;
`;

const TesterTreeView = styled.div`
    display: flex;
    flex-direction: column;
    width: 375px;

    border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};

    background: ${UIStyleConstants.WHITE_DARK_COLOR};

    box-sizing: border-box;
    border-radius: 4px;

    padding: 8px 12px;

    flex: 1;
    overflow: auto;
`;

const BuilderRender = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: auto;
`;

const TesterControls = styled.div`
    padding: 8px 20px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;

    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: 0em;
    text-align: left;

    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

    width: 375px;
`;

const TestActions = styled.div`
    display: flex;
    flex-direction: row;
`;

const TesterStatus = styled.div`
  padding: 4px 8px;

  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 17px;
  letter-spacing: 0px;
  text-align: left;
  color ${UIStyleConstants.GRAY_COLOR};;
`;

const TestedItemTitle = styled.div`
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 17px;
  letter-spacing: 0px;
  text-align: left;
  color ${UIStyleConstants.GRAY_COLOR};;
`;

const TesterSection = styled.div`
    cursor: pointer;
`;

const EventsRender = styled.div`
    display: flex;
    flex-direction: column;
`;

const EventRender = styled.div<{ type: EventLogType }>`
    color: ${(props) =>
        props.type === EventLogType.ERROR ? UIStyleConstants.RED_COLOR : UIStyleConstants.BLACK_COLOR};
`;

const RunButton = styled.div`
    display: flex;
    align-items: center;

    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 18px;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

    padding: 6px 12px;
    position: relative;

    cursor: pointer;

    &:hover {
        background-color: ${UIStyleConstants.GRAY_LIGHT_COLOR};
    }

    & > img {
        margin-right: -2px;
        margin-left: 8px;
    }
`;

const Render = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: row;

    background: ${UIStyleConstants.WHITE_DARK_COLOR};
    overflow: hidden;
`;
